import React, { useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Alert, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { SidebarContext } from "../Sidebar/SidebarProvider";

function unboxOrigin(raw) {
  const u = (raw || "").trim();
  if (!u) return "";
  try {
    return new URL(u).origin;
  } catch {
    return "";
  }
}

function openExternalUrl(url) {
  const href = String(url || "").trim();
  if (!href) return;
  try {
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.referrerPolicy = "no-referrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch {
    window.open(href, "_blank", "noopener,noreferrer");
  }
}

/**
 * Inserta UNIBOX (unibox-mail en otro puerto/origen) y le pasa el JWT del CRM vía postMessage.
 * Requiere REACT_APP_UNIBOX_MAIL_URL y que unibox-backend comparta TOKEN_SECRET con el CRM.
 */
export default function UniboxPage() {
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isStats = location.pathname === "/unibox-stats";
  const muiTheme = useTheme();
  const { contentMargin, sidebarWidth } = useContext(SidebarContext);
  const base = (process.env.REACT_APP_UNIBOX_MAIL_URL || "").trim();
  const childOrigin = unboxOrigin(base);

  useEffect(() => {
    if (!childOrigin) return undefined;

    function onMsg(e) {
      if (e.origin !== childOrigin) return;
      if (e.data?.type === "UNIBOX_AUTH_FAILED") {
        localStorage.removeItem("token");
        navigate("/login", { replace: true });
        return;
      }
      if (e.data?.type === "UNIBOX_OPEN_LINK" && typeof e.data.url === "string") {
        const url = e.data.url.trim();
        if (url) {
          openExternalUrl(url);
        }
      }
    }

    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [childOrigin, navigate]);

  const sendTokenToIframe = useCallback(() => {
    if (!childOrigin) return;
    const token = localStorage.getItem("token");
    const win = iframeRef.current?.contentWindow;
    if (!token || !win) return;
    win.postMessage(
      {
        type: "CRM_AUTH_TOKEN",
        token,
        themeMode: muiTheme.palette.mode,
      },
      childOrigin
    );
  }, [childOrigin, muiTheme.palette.mode]);

  const sendThemeToIframe = useCallback(() => {
    if (!childOrigin) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      { type: "CRM_THEME_MODE", mode: muiTheme.palette.mode },
      childOrigin
    );
  }, [childOrigin, muiTheme.palette.mode]);

  useEffect(() => {
    sendThemeToIframe();
  }, [sendThemeToIframe]);

  const onIframeLoad = useCallback(() => {
    sendTokenToIframe();
    setTimeout(sendTokenToIframe, 150);
    setTimeout(sendTokenToIframe, 500);
  }, [sendTokenToIframe]);

  const sidebarMainSx = {
    ...contentMargin,
    width: `calc(100% - ${sidebarWidth})`,
    maxWidth: `calc(100% - ${sidebarWidth})`,
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  if (!base) {
    return (
      <Box
        sx={{
          p: 2,
          ...sidebarMainSx,
          maxWidth: `min(720px, calc(100% - ${sidebarWidth}))`,
        }}
      >
        <Alert severity="warning">
          Falta{" "}
          <Typography component="span" sx={{ fontFamily: "monospace" }}>
            REACT_APP_UNIBOX_MAIL_URL
          </Typography>{" "}
          en el .env del CRM (ej.{" "}
          <Typography component="span" sx={{ fontFamily: "monospace" }}>
            http://localhost:3002
          </Typography>
          ). Descoméntala o créala apuntando al origen donde corre CRA de unibox-mail.
        </Alert>
      </Box>
    );
  }

  const src = `${base.replace(/\/+$/, "")}/?embedded=1${isStats ? "&view=stats" : ""}`;

  /** Misma URL que TON → el iframe cargaría el CRM otra vez (sidebars duplicados). */
  if (
    typeof window !== "undefined" &&
    childOrigin &&
    window.location.origin === childOrigin
  ) {
    return (
      <Box
        sx={{
          p: 2,
          ...sidebarMainSx,
          maxWidth: `min(720px, calc(100% - ${sidebarWidth}))`,
        }}
      >
        <Alert severity="error">
          La URL de UNIBOX ({base}) es la misma que esta ventana ({window.location.origin}).
          Arranca <strong>unibox-mail</strong> en otro puerto (p. ej. crea{" "}
          <Typography component="span" sx={{ fontFamily: "monospace" }}>
            PORT=3000
          </Typography>{" "}
          en <Typography component="span" sx={{ fontFamily: "monospace" }}>unibox-mail/.env</Typography>)
          y pon{" "}
          <Typography component="span" sx={{ fontFamily: "monospace" }}>
            REACT_APP_UNIBOX_MAIL_URL=http://localhost:3000
          </Typography>{" "}
          en el .env del CRM. Reinicia ambos <code>npm start</code>.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: "calc(100vh - 12px)",
        p: 0,
        ...sidebarMainSx,
      }}
    >
      <iframe
        ref={iframeRef}
        title={isStats ? "Estadísticas TONBOX" : "Bandeja UNIBOX"}
        src={src}
        onLoad={onIframeLoad}
        style={{ border: 0, width: "100%", height: "100%", display: "block" }}
        sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-downloads allow-modals allow-top-navigation-by-user-activation"
      />
    </Box>
  );
}
